import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useBaby } from '@/contexts/BabyContext';
import { StorageAdapter } from '@/lib/storage-adapter';
import { Card, CardPill } from './Card';
import { CardButton } from './CardButton';
import {
  ageInMonths,
  getCurrentSolidsWeek,
  getFoodsForWeek,
} from '@/lib/solidFoodSchedule';
import type { DietPreference } from '@/lib/solidFoodSchedule';

function getPrepDismissedKey(babyId: string) {
  return `solids_prep_dismissed_${babyId}`;
}

const SolidsCard = () => {
  const navigate = useNavigate();
  const { activeBaby } = useBaby();

  if (!activeBaby) return null;

  const babyAgeMonths = ageInMonths(activeBaby.dob);
  const firstName = activeBaby.name.split(' ')[0];

  // Under 5 months — nothing on homepage
  if (babyAgeMonths < 5) return null;

  const hasStarted = !!activeBaby.solidsStartDate;

  // ── Active solids: show planner entry ──────────────────────────────────────
  if (hasStarted) {
    const currentWeek = getCurrentSolidsWeek(activeBaby.solidsStartDate!);
    const dietPref = activeBaby.dietPreference as DietPreference | undefined;
    const weekFoods = getFoodsForWeek(currentWeek, dietPref, babyAgeMonths);
    const foodPreviews = weekFoods.slice(0, 3).map(f => f.emoji).join(' ');

    return (
      <button
        onClick={() => navigate('/solids')}
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-left active:scale-[0.99] transition-all"
        style={{
          background: 'hsl(var(--primary) / 0.06)',
          border: '1px solid hsl(var(--primary) / 0.15)',
        }}
      >
        <span className="text-xl">🥄</span>
        <div className="flex-1 min-w-0">
          <p style={{ fontSize: 14, fontWeight: 600, color: 'hsl(var(--foreground))' }}>
            Solids — Week {currentWeek}
          </p>
          <p className="text-caption text-muted-foreground">
            {foodPreviews} {weekFoods.length} food{weekFoods.length !== 1 ? 's' : ''} to try this week
          </p>
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
      </button>
    );
  }

  // ── Not started: check if dismissed from homepage ──────────────────────────
  const isDismissed = !!StorageAdapter.getItem(getPrepDismissedKey(activeBaby.id));
  if (isDismissed) return null;

  // ── Prep card — shown once until tapped ────────────────────────────────────
  const handleOpen = () => {
    StorageAdapter.setItem(getPrepDismissedKey(activeBaby.id), '1');
    navigate('/solids-guide');
  };

  return (
    <Card style={{ border: '1px solid hsl(var(--primary) / 0.18)' }}>
      <CardPill label="🥣 Prep for Solids" variant="orange" />
      <p className="font-bold text-foreground mt-2.5" style={{ fontSize: 15, lineHeight: '21px' }}>
        See what month 6 to 12 actually looks like
      </p>
      <p className="text-muted-foreground mt-1" style={{ fontSize: 12, lineHeight: '18px' }}>
        Understand how milk, meals, textures, and routine shift as {firstName} grows.
      </p>
      <div className="mt-3">
        <CardButton variant="cta" fullWidth onClick={handleOpen}>
          Open solids guide
        </CardButton>
      </div>
    </Card>
  );
};

export default SolidsCard;
